/**
 * The game's tooltip: an item, spell, NPC, quest, zone, object or recipe laid out the way the
 * client would draw it, on the character
 * window's dark palette. `GameTooltipBody` is the content; `GameTooltip` puts it next to its
 * anchor in a fixed-position portal so table scroll containers never clip it.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type {
  AbilityTooltipData,
  ItemTooltipData,
  NpcTooltipData,
  ObjectTooltipData,
  QuestTooltipData,
  RecipeTooltipData,
  TooltipResponse,
  ZoneTooltipData,
} from '@thottbot/shared';
import { Badge } from '../Badge';
import { Money } from '../../features/artifact/FieldTable';

/**
 * Tooltip palette, mirrored in `retro.css` (`.game-tooltip`). The game's quality hues, except
 * rare and epic, lightened from #0070dd / #a335ee (3.6:1) to read at 4.5:1 on the background.
 */
export const TOOLTIP_COLOURS = {
  background: '#1c1a17',
  text: '#f0e6c8',
  muted: '#b3a98f',
  green: '#1eff00',
  gold: '#ffd100',
  quality: ['#9d9d9d', '#ffffff', '#1eff00', '#2f8cff', '#b866f5', '#ff8000', '#e6cc80'],
} as const;

export interface TooltipKnown {
  name: string;
  quality?: number;
}

export interface TalentContext {
  rank: number;
  maxRank: number;
}

export type TooltipState = 'loading' | 'ready' | 'error';

export interface GameTooltipContent {
  known: TooltipKnown;
  state: TooltipState;
  data?: TooltipResponse;
  /** Notes about this particular use of the item (a build's enchant, source, alternative...). */
  contextLines?: string[];
  talent?: TalentContext;
  /** Body for things the database does not know: drawn as-is, nothing is fetched. */
  lines?: string[];
}

const BIND_TEXT: Record<string, string> = {
  BoP: 'Binds when picked up',
  BoE: 'Binds when equipped',
  BoU: 'Binds when used',
  Quest: 'Quest Item',
};

/** The game shows the five attributes in white and everything else (crit, spell power...) in green. */
const PRIMARY_STATS = new Set(['Strength', 'Agility', 'Stamina', 'Intellect', 'Spirit']);

function Row({ left, right, className }: { left: ReactNode; right?: ReactNode; className?: string }) {
  return (
    <div className={`gt-row${className ? ` ${className}` : ''}`}>
      <span>{left}</span>
      {right !== undefined && right !== null && right !== '' && <span className="gt-right">{right}</span>}
    </div>
  );
}

function ItemBody({ d }: { d: ItemTooltipData }) {
  const dps = d.damage ? ((d.damage.min + d.damage.max) / 2 / d.damage.speed).toFixed(1) : null;
  return (
    <>
      {d.bind && d.bind !== 'None' && <Row left={BIND_TEXT[d.bind] ?? d.bind} />}
      {(d.slot || d.subclass) && <Row left={d.slot} right={d.subclass} />}
      {d.damage && <Row left={`${d.damage.min} - ${d.damage.max} Damage`} right={`Speed ${d.damage.speed.toFixed(2)}`} />}
      {dps && <Row left={`(${dps} damage per second)`} />}
      {d.armor ? <Row left={`${d.armor} Armor`} /> : null}
      {d.stats?.map((s) => (
        <Row key={s.name} className={PRIMARY_STATS.has(s.name) ? undefined : 'gt-green'} left={`${s.value > 0 ? '+' : ''}${s.value} ${s.name}`} />
      ))}
      {d.classes && d.classes.length > 0 && <Row left={`Classes: ${d.classes.join(', ')}`} />}
      {d.races && d.races.length > 0 && <Row left={`Races: ${d.races.join(', ')}`} />}
      {d.requiredLevel ? <Row left={`Requires Level ${d.requiredLevel}`} /> : null}
      {d.requiredProfession && <Row left={`Requires ${d.requiredProfession.name} (${d.requiredProfession.skill})`} />}
      {d.pvpRank && <Row left={`Requires PvP rank ${d.pvpRank}`} />}
      {d.use?.text && <Row className="gt-green" left={`Use: ${d.use.text}`} />}
      {d.description && <Row className="gt-gold" left={`"${d.description}"`} />}
      {d.itemLevel ? <Row className="gt-muted" left={`Item Level ${d.itemLevel}`} /> : null}
      {d.sellPrice ? (
        <Row
          left={
            <>
              Sell Price: <Money copper={d.sellPrice} />
            </>
          }
        />
      ) : null}
    </>
  );
}

function AbilityBody({ d, talent }: { d: AbilityTooltipData; talent?: TalentContext }) {
  const rankText = (rank: number) => d.ranks?.find((r) => r.rank === rank)?.description;
  const current = talent && talent.rank > 0 ? (rankText(talent.rank) ?? d.description) : talent ? undefined : d.description;
  const next = talent && talent.rank < talent.maxRank ? (rankText(talent.rank + 1) ?? (talent.rank === 0 ? d.description : undefined)) : undefined;
  const rankLabel = talent ? `Rank ${talent.rank}/${talent.maxRank}` : d.rank ? `Rank ${d.rank}` : undefined;
  return (
    <>
      {(rankLabel || d.tree) && <Row left={rankLabel} right={d.tree} />}
      {(d.cost || d.range) && <Row left={d.cost} right={d.range} />}
      {(d.castTime || d.cooldown) && <Row left={d.castTime} right={d.cooldown} />}
      {d.passive && <Row left="Passive" />}
      {d.level ? <Row left={`Requires Level ${d.level}`} /> : null}
      {current && <Row className="gt-gold" left={current} />}
      {next && (
        <div className="gt-next">
          <div>Next rank:</div>
          <div className="gt-gold">{next}</div>
        </div>
      )}
    </>
  );
}

/** "Level 10", or "Level 10-12" for a range. */
function levelRange(min?: number, max?: number): string | undefined {
  const lo = min ?? max;
  const hi = max ?? min;
  if (lo === undefined || hi === undefined) return undefined;
  return lo === hi ? `Level ${lo}` : `Level ${lo}-${hi}`;
}

const NPC_SERVICES = { quests: 'Quest giver', vendor: 'Vendor', trainer: 'Trainer' } as const;

function NpcBody({ d }: { d: NpcTooltipData }) {
  const level = levelRange(d.levelMin, d.levelMax);
  return (
    <>
      {(level || d.creatureType) && <Row left={level} right={d.creatureType} />}
      {d.faction && <Row left={d.faction} />}
      {d.zone && <Row className="gt-muted" left={d.zone} />}
      {d.services?.map((s) => <Row key={s} className="gt-gold" left={NPC_SERVICES[s]} />)}
    </>
  );
}

function QuestBody({ d }: { d: QuestTooltipData }) {
  const r = d.rewards;
  return (
    <>
      {(d.level || d.zone) && <Row left={d.level ? `Level ${d.level}` : undefined} right={d.zone} />}
      {d.minLevel ? <Row left={`Requires Level ${d.minLevel}`} /> : null}
      {d.faction && d.faction !== 'Both' && <Row left={d.faction} />}
      {d.giver && <Row left={`Starts: ${d.giver}`} />}
      {d.turnIn && <Row left={`Ends: ${d.turnIn}`} />}
      {d.objectives && <Row className="gt-gold" left={d.objectives} />}
      {r && (
        <div className="gt-rewards">
          {(r.xp || r.money) && (
            <Row
              left={r.xp ? `${r.xp} XP` : undefined}
              right={r.money ? <Money copper={r.money} /> : undefined}
            />
          )}
          {r.items && r.items.length > 0 && <Row left={`Rewards: ${r.items.join(', ')}`} />}
          {r.choiceItems && r.choiceItems.length > 0 && <Row className="gt-green" left={`Choose one: ${r.choiceItems.join(', ')}`} />}
        </div>
      )}
    </>
  );
}

function ZoneBody({ d }: { d: ZoneTooltipData }) {
  const level = levelRange(d.levelMin, d.levelMax);
  return (
    <>
      {(d.continent || d.faction) && <Row left={d.continent} right={d.faction} />}
      {level && <Row left={level} />}
      {d.description && <Row className="gt-gold" left={d.description} />}
    </>
  );
}

function ObjectBody({ d }: { d: ObjectTooltipData }) {
  return (
    <>
      {(d.objectType || d.zone) && <Row left={d.objectType} right={d.zone} />}
      {d.contains ? <Row left={`Contains ${d.contains} ${d.contains === 1 ? 'item' : 'items'}`} /> : null}
      {d.services?.includes('quests') && <Row className="gt-gold" left="Starts quests" />}
    </>
  );
}

function RecipeBody({ d }: { d: RecipeTooltipData }) {
  const reagents = d.reagents?.map((r) => (r.count ? `${r.name} x${r.count}` : r.name)).join(', ');
  return (
    <>
      {d.profession && <Row left={`Requires ${d.profession}${d.requiredSkill ? ` (${d.requiredSkill})` : ''}`} />}
      {d.skillColors && <Row className="gt-muted" left={`Skill-up: ${d.skillColors.yellow}/${d.skillColors.green}/${d.skillColors.grey}`} />}
      {reagents && <Row left={`Reagents: ${reagents}`} />}
      {d.result && <Row className="gt-green" left={`Creates: ${d.result}`} />}
      {d.enchant && <Row className="gt-green" left={`Enchant: ${d.enchant}`} />}
      {d.source && <Row className="gt-muted" left={`Source: ${d.source}`} />}
    </>
  );
}

/** The tooltip's content: name, the fetched (or static) body, then any context lines. */
export function GameTooltipBody({ known, state, data, contextLines, talent, lines }: GameTooltipContent) {
  const name = data?.name ?? known.name;
  const quality = data?.type === 'item' ? (data.data.quality ?? known.quality) : known.quality;
  return (
    <div className="gt-body" data-testid="game-tooltip-body">
      <div className={`gt-name${quality !== undefined ? ` q-${quality}` : ''}`}>{name}</div>
      {state === 'ready' && data?.type === 'item' && <ItemBody d={data.data} />}
      {state === 'ready' && data?.type === 'ability' && <AbilityBody d={data.data} talent={talent} />}
      {state === 'ready' && data?.type === 'npc' && <NpcBody d={data.data} />}
      {state === 'ready' && data?.type === 'quest' && <QuestBody d={data.data} />}
      {state === 'ready' && data?.type === 'zone' && <ZoneBody d={data.data} />}
      {state === 'ready' && data?.type === 'object' && <ObjectBody d={data.data} />}
      {state === 'ready' && data?.type === 'recipe' && <RecipeBody d={data.data} />}
      {!data && talent && <Row left={`Rank ${talent.rank}/${talent.maxRank}`} />}
      {lines?.map((l, i) => <Row key={i} left={l} />)}
      {state === 'error' && <Row className="gt-muted" left="Details unavailable" />}
      {data?.dataStatus === 'forever_beta' && (
        <div className="gt-badge">
          <Badge status={data.dataStatus} build={data.sourceBuild} />
        </div>
      )}
      {contextLines && contextLines.length > 0 && (
        <div className="gt-context">
          {contextLines.map((l, i) => (
            <Row key={i} left={l} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface AnchorRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const GAP = 8;
const EDGE = 8;

/**
 * Below-right of the anchor; above it when that would run off the bottom, and always shifted
 * to stay `EDGE` px inside the viewport so the page never scrolls sideways.
 */
export function placeTooltip(anchor: AnchorRect, size: { width: number; height: number }, viewport: { width: number; height: number }) {
  let left = anchor.right + GAP;
  if (left + size.width > viewport.width - EDGE) left = anchor.left - GAP - size.width;
  left = Math.max(EDGE, Math.min(left, viewport.width - EDGE - size.width));
  let top = anchor.bottom + GAP;
  if (top + size.height > viewport.height - EDGE) top = anchor.top - GAP - size.height;
  top = Math.max(EDGE, top);
  return { left, top };
}

export function GameTooltip({ id, anchor, ...content }: GameTooltipContent & { id: string; anchor: AnchorRect }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    setPos(placeTooltip(anchor, { width: box.width, height: box.height }, { width: window.innerWidth, height: window.innerHeight }));
    // Re-measure when the body changes size (loading -> ready).
  }, [anchor, content.state, content.data]);
  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      className="game-tooltip"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
    >
      <GameTooltipBody {...content} />
    </div>,
    document.body,
  );
}
